import React from 'react';
import Heading from "components/common/Heading/Heading";
import Text from 'components/common/Text/Text';
import Quote from "components/stories/Body/Quote";
import { breakpoints } from "themes/breakpoints.theme";
import { getImage, scrollWithOffset, getUrlLocale } from './helper';

const renderParagraph = (content, quote, quotePosition, page) => {
    const createText = (data) => {
        return data.map((item) => {
          let element = item.text;
           //id
          if (item.id) {
            element = <span id={item.id}>{element}</span>
          }
          //anchor link
          else if (item.anchorLink) {
            element = <a href={item.anchorLink} onClick={(e) => scrollWithOffset(e, item.anchorLink.replace('#',''))}>{element}</a>
          }
          // Link
          if (item.href) {
            element = React.createElement(
              "a",
              { href: item.href, target: "_blank", ...(item.nofollow) && {rel: "nofollow"} },
              element
            );
          }
          // Styles
          if (item.marks && item.marks.length > 0) {
            item.marks.forEach((mark) => {
              let styleTag = 'span';
              switch(mark){
                case 'strong':
                case 'em':
                  styleTag = mark;
                  break;
                case 'underline':
                  styleTag = 'u';
                  break;
                case 'strike-through':
                  styleTag = 's';
                  break;
                default:
                  break;
              }
              element = React.createElement(styleTag, null, element);
            });
          }
          return element;
        });
      };
      let result = [];
      /* This contentCount is needed to show quote in the right position
       ** ol/ul items are combined in a paragrahp sometimes. */
      let contentCount = 0;
      // eslint-disable-next-line
      content.map((paragraph, i) => {
        // Content
        let tag;
        switch (paragraph.style) {
          case "h1":
            tag = (page === 'story') ? {name: 'Heading', level: 'h4'} : { name: "Heading", level: paragraph.style };
            break;
          case "h2":
          case "h3":
          case "h4":
          case "h5":
          case "h6":
            tag = { name: "Heading", level: paragraph.style };
            break;
          case "ul":
          case "ol":
            tag = { name: paragraph.style };
            break;
          default:
            tag = { name: "p" };
            break;
        }
    
        // If the data element isn't an array, make it one
        if (!Array.isArray(paragraph.data)) {
          paragraph.data = [paragraph.data];
        }
    
        // In case of rendering ordered list (ol) or unordered list (ul)
        // Have to create ol/ul element with li childs so it could display correct order numbers
        // Have to check next comming elements are still in ol/ul tag
        if (tag.name === "ol" || tag.name === "ul") {
          const currentListType = tag.name;
          // Do not render if this list item is already rendered in previous element.. i.e..previous element is also list item
          if (content[i - 1] && content[i - 1].style === currentListType) {
            // eslint-disable-next-line
            return;
          }
          //Check next element
          let li = [React.createElement("li", null, createText(content[i].data))];
          let index = i + 1;
          while (content[index] && content[index].style === currentListType) {
            li.push(
              React.createElement("li", null, createText(content[index].data))
            );
            index++;
            contentCount++;
          }
          result.push(React.createElement(currentListType, { key: i }, li));
        } else {
          let elementType = tag.name;
          if(tag.name === 'Heading'){
            elementType = Heading;
          }else if(tag.name === 'p'){
            elementType = Text;
          }
          // Other Tags
          let currentElement = React.createElement(
            elementType,
            tag.level ? { key: i, level: tag.level } : { key: i,  tag: (tag.name === 'p')? 'p': undefined },
            createText(paragraph.data)
          );
          result.push(currentElement);
        }
    
        // Quote
        if (quote) {
          if (quotePosition && quotePosition === contentCount + 1) {
            result.push(<Quote content={quote} key={i} />);
          }
          if (!quotePosition && contentCount === content.length - 1) {
            result.push(<Quote content={quote} key={i} />);
          }
        }
    
        contentCount++;
      });
    
      return result;
}

const renderPictureTag = (image, size) =>
  <picture>
    <source
      media={`(min-width:${breakpoints.md})`}
      srcSet={getImage(image, size === "small" ? "medium" : "large")}
      type="image/webp"
    />
    <source
      media={`(min-width:${breakpoints.md})`}
      srcSet={getImage(image, size === "small" ? "medium" : "large", true)}
      type={getImageFormat(image)}
    />
    <source
      media={`(min-width:${breakpoints.sm})`}
      srcSet={getImage(image, "medium")}
      type="image/webp"
    />
    <source
      media={`(min-width:${breakpoints.sm})`}
      srcSet={getImage(image, "medium", true)}
      type={getImageFormat(image)}
    />
    <source
      media={`(min-width:${breakpoints.xs})`}
      srcSet={getImage(image, "small")}
      type="image/webp"
    />
    <source
      media={`(min-width:${breakpoints.xs})`}
      srcSet={getImage(image, "small", true)}
      type={getImageFormat(image)}
    />
    <img
      src={getImage(image, "org", true)}
      alt={image && image.alt ? image.alt : "no-alt"}
    />
  </picture>;

function renderImage(image, size) {
  return image ? image.link ? <a href={image.link} target="_blank" rel="noreferrer">{renderPictureTag(image, size)}</a>
  :renderPictureTag(image, size)
  : null;
}

function getImageFormat(item) {
  let value = "";
  if (item && item.urls && item.urls.original && item.urls.original.original) {
    let dotIndex = item.urls.original.original.lastIndexOf(".");
    value = "image/" + item.urls.original.original.substring(dotIndex + 1);
  }
  return value;
}

function renderCurrency(options, locale) {
  let thisLocale = (locale && typeof locale === 'string') ? locale : `${getUrlLocale().language}-${getUrlLocale().country}`;
  let config = {
    value: options.amount ? options.amount : 0,
    currency: options.currency ? options.currency : 'AUD',
    symbol: options.symbol ? options.symbol : '$',
    format: locale,
    precision: 0,
    position: true, // Symbol position: true - left, false - right
    space: false // Space between symbol and amount
  };

  let getFormat = (value) => {
    const formatter = new Intl.NumberFormat();
    if (typeof value === "number") {
      return formatter.format(value);
    } else if (typeof value === "string") {
      const targetValue = parseInt(value);
      if (!Number.isNaN(targetValue)) {
        return formatter.format(targetValue);
      }
    }
    return value;
  };

  // Format currency string based on locale
  switch(thisLocale.toLowerCase()){
    case 'fr-ca':
      config.position = false;
      config.space = true;
      break;
    default:
      break;
  }

 return `${config.position ? config.symbol : ''}${config.space ? ' ' : ''}${getFormat(config.value)}${config.space ? ' ' : ''}${!config.position ? config.symbol : ''}`;
}
 
export { renderParagraph, renderImage, renderCurrency };
